import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { axiosGet, axiosPost, axiosPut, axiosDelete } from '../api/axios';
import { Resource } from './useResources';
import { ListenerAudio, ListenerRole } from './useListenerAudio';
import { Chatbot } from './useChatBot';
import { toast } from 'react-toastify';
import { buildItemsPayload } from '../containers/content-library/utils/collection-helpers';

export const contentCollectionQueryKeys = {
  all: ['contentCollections'] as const,
  list: (params: ContentCollectionListRequest) => [...contentCollectionQueryKeys.all, 'list', params] as const,
  detail: (collectionId: number) => [...contentCollectionQueryKeys.all, 'detail', collectionId] as const,
};

export type CollectionItemType = 'resource' | 'audio' | 'video' | 'listener' | 'chatbot' | 'unknown';

interface CollectionItemApiResponse {
  id: number;
  created_at: string;
  sort_order: number;
  resource_id?: number | null;
  listener_role_id?: number | null;
  listener_audio_id?: number | null;
  chatbot_id?: number | null;
  data?: Resource | ListenerAudio | ListenerRole | Chatbot | Record<string, unknown> | null;
}

export interface ContentCollectionVertical {
  id: number;
  key: string;
  name: string;
  description?: string | null;
}

interface ContentCollectionTag {
  id: number;
  name: string;
  tag_type?: string | null;
}

interface ContentCollectionDomain {
  id: number;
  name: string;
  key?: string;
}

interface ContentCollectionClient {
  id: number;
  name?: string | null;
  sort_order?: number;
}

interface ContentCollectionApiResponse {
  id: number;
  created_at: string;
  name: string;
  description?: string | null;
  visibility: boolean;
  is_public?: boolean;
  public_key?: string | null;
  client_id?: number | null;
  client_name?: string | null;
  clients?: ContentCollectionClient[] | null;
  domain_id?: number | null;
  domain_name?: string | null;
  domain?: ContentCollectionDomain | null;
  sort_order?: number;
  items?: CollectionItemApiResponse[] | null;
  verticals?: ContentCollectionVertical[] | null;
  tags?: ContentCollectionTag[] | null;
}

interface ContentCollectionListApiResponse {
  count: number;
  data: ContentCollectionApiResponse[];
}

export interface CollectionItem extends CollectionItemApiResponse {
  contentType: CollectionItemType;
}

export interface ContentCollection extends ContentCollectionApiResponse {
  items: CollectionItem[];
  verticals: ContentCollectionVertical[];
  tags: ContentCollectionTag[];
  clients: ContentCollectionClient[];
}

export interface ContentCollectionListResponse {
  count: number;
  data: ContentCollection[];
}

export interface ContentCollectionListRequest {
  page?: number;
  limit?: number;
  search?: string;
  order_by?: 'created' | 'name' | 'sort_order';
  order_direction?: 'asc' | 'desc';
  visibility?: boolean;
  tag_groups?: number[];
  vertical_ids?: number[];
  domain_ids?: number[];
  client_ids?: number[];
}

export interface CollectionItemUpdate {
  id?: number; // Include ID to preserve existing items when reordering
  resource_id?: number | null;
  listener_audio_id?: number | null;
  listener_role_id?: number | null;
  chatbot_id?: number | null;
  sort_order: number;
}

export interface ContentCollectionUpdatePayload {
  name?: string;
  description?: string | null;
  visibility?: boolean;
  is_public?: boolean;
  client_id?: number | null;
  client_ids?: number[] | null;
  domain_id?: number | null;
  items?: CollectionItemUpdate[];
  vertical_ids?: number[] | null;
  tag_ids?: number[] | null;
}

export interface ContentCollectionCreatePayload {
  name: string;
  description?: string | null;
  visibility: boolean;
  caller_role_id?: number | null;
  client_id?: number | null;
  client_ids?: number[] | null;
  domain_id?: number | null;
  vertical_ids?: number[] | null;
  tag_ids?: number[] | null;
}

const determineContentType = (item: CollectionItemApiResponse): CollectionItemType => {
  if (item.resource_id) {
    return 'resource';
  }

  if (item.listener_audio_id) {
    const audioData = item.data as ListenerAudio | undefined;
    if (audioData && audioData.video_id) {
      return 'video';
    }
    return 'audio';
  }

  if (item.listener_role_id) {
    return 'listener';
  }

  if (item.chatbot_id) {
    return 'chatbot';
  }

  return 'unknown';
};

const normalizeCollection = (collection: ContentCollectionApiResponse): ContentCollection => {
  const items = (collection.items || []).map((item) => ({
    ...item,
    resource_id: item.resource_id ?? null,
    listener_role_id: item.listener_role_id ?? null,
    listener_audio_id: item.listener_audio_id ?? null,
    chatbot_id: item.chatbot_id ?? null,
    data: item.data ?? null,
    contentType: determineContentType(item),
  }));

  items.sort((a, b) => a.sort_order - b.sort_order);

  const verticals = (collection.verticals || []).map((vertical) => ({
    ...vertical,
    description: vertical.description ?? null,
  }));

  const tags = (collection.tags || []).map((tag) => ({
    id: tag.id,
    name: tag.name,
    tag_type: tag.tag_type ?? null,
  }));

  const clients =
    collection.clients && collection.clients.length > 0
      ? collection.clients.map((c) => ({
          id: c.id,
          name: c.name ?? null,
          sort_order: c.sort_order ?? 0,
        }))
      : collection.client_id
        ? [{ id: collection.client_id, name: collection.client_name ?? null, sort_order: collection.sort_order ?? 0 }]
        : [];

  return {
    ...collection,
    description: collection.description ?? null,
    sort_order: collection.sort_order ?? 0,
    client_id: clients[0]?.id ?? collection.client_id ?? null,
    client_name: clients[0]?.name ?? collection.client_name ?? null,
    clients,
    items,
    verticals,
    tags,
  };
};

const getCollections = async (params: ContentCollectionListRequest = {}): Promise<ContentCollectionListResponse> => {
  const { page = 1, limit = 20, tag_groups, vertical_ids, domain_ids, client_ids, ...rest } = params;
  // Backend get_limit_offset() reads `page` + `limit` from query params and computes offset itself;
  // sending `offset` is silently ignored, which previously broke pagination.
  const queryParams: Record<string, string | number | undefined> = { page, limit, ...rest };
  if (tag_groups?.length) queryParams.tag_groups = tag_groups.join(',');
  if (vertical_ids?.length) queryParams.vertical_ids = vertical_ids.join(',');
  if (domain_ids?.length) queryParams.domain_ids = domain_ids.join(',');
  if (client_ids?.length) queryParams.client_ids = client_ids.join(',');
  const response = await axiosGet('/content_collections/?admin_override=true', queryParams, 'v3');
  const data = response.data as ContentCollectionListApiResponse;

  return {
    count: data.count,
    data: data.data.map(normalizeCollection),
  };
};

const getCollection = async (collectionId: number): Promise<ContentCollection> => {
  const response = await axiosGet(`/content_collections/${collectionId}?admin_override=true`, null, 'v3');
  const data = response.data as ContentCollectionApiResponse;
  return normalizeCollection(data);
};

const createCollectionRequest = async (payload: ContentCollectionCreatePayload): Promise<ContentCollection> => {
  const response = await axiosPost('/content_collections/', payload, 'v3');
  const data = response.data as ContentCollectionApiResponse;
  return normalizeCollection(data);
};

const updateCollectionRequest = async ({
  collectionId,
  payload,
}: {
  collectionId: number;
  payload: ContentCollectionUpdatePayload;
}): Promise<ContentCollection> => {
  const response = await axiosPut(`/content_collections/${collectionId}?admin_override=true`, payload, 'v3');
  const data = response.data as ContentCollectionApiResponse;
  return normalizeCollection(data);
};

export const useContentCollections = (params: ContentCollectionListRequest, options?: { enabled?: boolean }) => {
  const queryKey = contentCollectionQueryKeys.list(params);

  return useQuery<ContentCollectionListResponse>({
    queryKey,
    queryFn: () => getCollections(params),
    enabled: options?.enabled ?? true,
  });
};

export const useContentCollection = (collectionId?: number, options?: { enabled?: boolean }) => {
  const enabled = !!collectionId && (options?.enabled ?? true);
  const queryKey = collectionId
    ? contentCollectionQueryKeys.detail(collectionId)
    : ([...contentCollectionQueryKeys.all, 'detail', 'placeholder'] as const);

  return useQuery<ContentCollection>({
    queryKey,
    queryFn: () => getCollection(collectionId!),
    enabled,
  });
};

export const useUpdateContentCollection = () => {
  const queryClient = useQueryClient();

  return useMutation<
    ContentCollection,
    unknown,
    { collectionId: number; payload: ContentCollectionUpdatePayload },
    { previousCollection: ContentCollection | undefined }
  >({
    mutationFn: (args: { collectionId: number; payload: ContentCollectionUpdatePayload }) =>
      updateCollectionRequest(args),
    onMutate: async ({ collectionId, payload }) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: contentCollectionQueryKeys.detail(collectionId) });

      // Snapshot the previous value
      const previousCollection = queryClient.getQueryData<ContentCollection>(
        contentCollectionQueryKeys.detail(collectionId),
      );

      // Optimistically update items order if items are being updated
      if (payload.items && previousCollection) {
        const updatedItems = previousCollection.items.map((item) => {
          const updatedItem = payload.items?.find((pi) => pi.id === item.id);
          if (updatedItem) {
            return { ...item, sort_order: updatedItem.sort_order };
          }
          return item;
        });
        // Sort by the new sort_order
        updatedItems.sort((a, b) => a.sort_order - b.sort_order);

        queryClient.setQueryData(contentCollectionQueryKeys.detail(collectionId), {
          ...previousCollection,
          items: updatedItems,
        });
      }

      return { previousCollection };
    },
    onSuccess: (data, variables) => {
      toast.success('Collection updated');
      queryClient.setQueryData(contentCollectionQueryKeys.detail(variables.collectionId), data);
      queryClient.invalidateQueries({ queryKey: contentCollectionQueryKeys.all });
    },
    onError: (_error, variables, context) => {
      toast.error('Failed to update collection');
      // Rollback to the previous value on error
      if (context?.previousCollection) {
        queryClient.setQueryData(contentCollectionQueryKeys.detail(variables.collectionId), context.previousCollection);
      }
    },
  });
};

export const useCreateContentCollection = () => {
  const queryClient = useQueryClient();

  return useMutation<ContentCollection, unknown, ContentCollectionCreatePayload>({
    mutationFn: (payload: ContentCollectionCreatePayload) => createCollectionRequest(payload),
    onSuccess: (data) => {
      toast.success('Collection created');
      queryClient.invalidateQueries({ queryKey: contentCollectionQueryKeys.all });
      queryClient.invalidateQueries({ queryKey: contentCollectionQueryKeys.detail(data.id) });
    },
    onError: () => {
      toast.error('Failed to create collection');
    },
  });
};

export const useDuplicateContentCollection = () => {
  const queryClient = useQueryClient();

  return useMutation<
    ContentCollection,
    unknown,
    { source: ContentCollection; payload: ContentCollectionCreatePayload }
  >({
    mutationFn: async ({ source, payload }) => {
      const created = await createCollectionRequest(payload);
      if (!source.items.length) {
        return created;
      }
      // Strip source ids so the backend creates net-new rows for the duplicate.
      const items = buildItemsPayload(source.items).map(({ id: _id, ...rest }) => rest);
      return updateCollectionRequest({
        collectionId: created.id,
        payload: { items },
      });
    },
    onSuccess: (data) => {
      toast.success('Collection duplicated');
      queryClient.invalidateQueries({ queryKey: contentCollectionQueryKeys.all });
      queryClient.setQueryData(contentCollectionQueryKeys.detail(data.id), data);
    },
    onError: () => {
      toast.error('Failed to duplicate collection');
    },
  });
};

const deleteCollectionRequest = async (collectionId: number): Promise<void> => {
  await axiosDelete(`/content_collections/${collectionId}?admin_override=true`, null, 'v3');
};

const reorderCollectionsRequest = async (collectionIds: number[], clientId: number): Promise<ContentCollection[]> => {
  const response = await axiosPut(
    '/content_collections/sort_order',
    { collection_ids: collectionIds, client_id: clientId },
    'v3',
  );
  const data = response.data as ContentCollectionApiResponse[];
  return data.map(normalizeCollection);
};

export const useReorderContentCollections = () => {
  const queryClient = useQueryClient();

  return useMutation<
    ContentCollection[],
    unknown,
    { collectionIds: number[]; clientId: number; listParams: ContentCollectionListRequest },
    { previousList: ContentCollectionListResponse | undefined }
  >({
    mutationFn: ({ collectionIds, clientId }) => reorderCollectionsRequest(collectionIds, clientId),
    onMutate: async ({ collectionIds, listParams }) => {
      const listKey = contentCollectionQueryKeys.list(listParams);
      await queryClient.cancelQueries({ queryKey: listKey });

      const previousList = queryClient.getQueryData<ContentCollectionListResponse>(listKey);
      if (previousList) {
        const byId = new Map(previousList.data.map((c) => [c.id, c]));
        const reordered = collectionIds
          .map((id, index) => {
            const collection = byId.get(id);
            return collection ? { ...collection, sort_order: index } : null;
          })
          .filter((c): c is ContentCollection => c != null);

        queryClient.setQueryData(listKey, {
          ...previousList,
          data: reordered,
        });
      }

      return { previousList };
    },
    onSuccess: () => {
      toast.success('Collection order updated');
      queryClient.invalidateQueries({ queryKey: contentCollectionQueryKeys.all });
    },
    onError: (_error, variables, context) => {
      toast.error('Failed to update collection order');
      if (context?.previousList) {
        queryClient.setQueryData(contentCollectionQueryKeys.list(variables.listParams), context.previousList);
      }
    },
  });
};

export const useDeleteContentCollection = () => {
  const queryClient = useQueryClient();

  return useMutation<void, unknown, number>({
    mutationFn: (collectionId: number) => deleteCollectionRequest(collectionId),
    onSuccess: (_data, collectionId) => {
      toast.success('Collection deleted');
      queryClient.invalidateQueries({ queryKey: contentCollectionQueryKeys.all });
      queryClient.removeQueries({ queryKey: contentCollectionQueryKeys.detail(collectionId) });
    },
    onError: () => {
      toast.error('Failed to delete collection');
    },
  });
};
